import React from "react";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";

import Wrapper from "../components/layout/Wrapper";
import useConnectedClients from "../hooks/useConnectedClients";

// const SIXTY_SEC_MS = 60_000;

function rowSx(row) {
   const connected = Boolean(row.connected);
   if (connected) {
      return {
         bgcolor: "#c8e6c9",
         "& td": { color: "#1b5e20", borderColor: "rgba(0,0,0,0.12)" },
      };
   }

   // socketLog disabled (stale / last-response coloring):
   // const lastResp = parseSocketLogDate(socketLogTs(row, "lastUpdateResponse", "last_update_response"));
   // const lr = lastResp ? lastResp.getTime() : NaN;
   // const now = Date.now();
   // if (Number.isFinite(lr)) {
   //    const ageMs = now - lr;
   //    if (ageMs >= 0 && ageMs <= SIXTY_SEC_MS) {
   //       return {
   //          bgcolor: "#ffe0b2",
   //          "& td": { color: "#e65100", borderColor: "rgba(0,0,0,0.12)" },
   //       };
   //    }
   // }

   return {
      bgcolor: "#ffcdd2",
      "& td": { color: "#b71c1c", borderColor: "rgba(0,0,0,0.12)" },
   };
}

// /**
//  * SQLite often returns naive "YYYY-MM-DD HH:mm:ss".
//  * Sequelize persists DATE strings in UTC semantics; constructing with `Date(y,m,d,...)` treated those as browser-local clock, skewing displays.
//  * Use UTC components here; `toLocaleTimeString` converts to the viewer's timezone.
//  */
// function parseSocketLogDate(value) {
//    if (value == null || value === "") {
//       return null;
//    }
//    if (value instanceof Date) {
//       const t = value.getTime();
//       return Number.isNaN(t) ? null : value;
//    }
//    if (typeof value === "number" && Number.isFinite(value)) {
//       const d = new Date(value);
//       return Number.isNaN(d.getTime()) ? null : d;
//    }

//    const s = String(value).trim();

//    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s)) {
//       const parts = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?/.exec(s);
//       if (parts) {
//          const [, yr, mo, da, hh, mi, sec] = parts;
//          return new Date(
//             Date.UTC(
//                Number(yr),
//                Number(mo) - 1,
//                Number(da),
//                Number(hh),
//                Number(mi),
//                Number(sec ?? 0)
//             )
//          );
//       }
//    }

//    const d = new Date(s);
//    return Number.isNaN(d.getTime()) ? null : d;
// }

// /** Localized time only (browser locale); no calendar date. */
// function formatSocketLogDatetime(v) {
//    const d = parseSocketLogDate(v);
//    if (!d) {
//       return v != null && v !== "" ? String(v) : "—";
//    }
//    return d.toLocaleTimeString(undefined, {
//       hour: "numeric",
//       minute: "2-digit",
//       second: "2-digit",
//    });
// }

// /** Read camelCase field or common snake_case from SQL (if underscoring ever used). */
// function socketLogTs(row, camelKey, snakeKey) {
//    const v = row[camelKey] ?? row[snakeKey];
//    return v;
// }

/** Feature match tables 1–4 → A–D; other numeric ids unchanged. */
function formatTableLabel(tableId) {
   if (tableId == null || tableId === "") {
      return "—";
   }
   const n = Number(tableId);
   if (!Number.isFinite(n) || !Number.isInteger(n)) {
      return String(tableId);
   }
   const map = { 1: "A", 2: "B", 3: "C", 4: "D" };
   if (map[n]) {
      return map[n];
   }
   return String(n);
}

export default function ConnectedClients() {
   const [rows, { loading, error, refresh }] = useConnectedClients();

   return (
      <Wrapper key='connectedClients'>
         <Typography
            variant='h3'
            component='div'
            sx={{ textAlign: "center", textTransform: "uppercase", mb: 2 }}
         >
            Connected Clients
         </Typography>

         <Typography variant='body2' sx={{ mb: 2, textAlign: "center", opacity: 0.85 }}>
            Green = live connection · Red = disconnected (socketLog / timing columns disabled)
         </Typography>

         <Stack direction='row' justifyContent='center' sx={{ mb: 2 }}>
            <Button variant='outlined' onClick={refresh} disabled={loading}>
               Refresh
            </Button>
         </Stack>

         {loading && (
            <Stack alignItems='center' sx={{ py: 4 }}>
               <CircularProgress />
            </Stack>
         )}

         {error && (
            <Typography color='error' sx={{ textAlign: "center", mb: 2 }}>
               {error}
            </Typography>
         )}

         {!loading && rows.length === 0 && !error && (
            <Typography sx={{ textAlign: "center" }}>No socket log rows.</Typography>
         )}

         {rows.length > 0 && (
            <Box sx={{ overflowX: "auto", px: { xs: 0, sm: 2 } }}>
               <Table size='small' sx={{ minWidth: 720 }}>
                  <TableHead sx={{ bgcolor: "#333" }}>
                     <TableRow>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                           Table
                        </TableCell>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                           IP Address
                        </TableCell>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Socket</TableCell>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                           Connected
                        </TableCell>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Last Send</TableCell>
                        <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Received</TableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {rows.map((row) => (
                        <TableRow key={row.id} sx={rowSx(row)}>
                           <TableCell>{formatTableLabel(row.tableId)}</TableCell>
                           <TableCell>{row.clientIp ?? "—"}</TableCell>
                           <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                              {row.socketId}
                           </TableCell>
                           {/* socketLog datetime formatters disabled; raw values if present */}
                           <TableCell>{row.connectedAt ?? row.connected_at ?? "—"}</TableCell>
                           <TableCell>
                              {row.lastUpdateBroadcast ?? row.last_update_broadcast ?? "—"}
                           </TableCell>
                           <TableCell>
                              {row.lastUpdateResponse ?? row.last_update_response ?? "—"}
                           </TableCell>
                        </TableRow>
                     ))}
                  </TableBody>
               </Table>
            </Box>
         )}
      </Wrapper>
   );
}
